/**
 * Firebase Crashlytics Service
 * Handles crash reporting with environment-based gating
 */

import { FirebaseCrashlytics } from "@capacitor-firebase/crashlytics";
import { CURRENT_ENV } from "./api";

/**
 * Check if analytics should be enabled (production only)
 */
const isAnalyticsEnabled = (): boolean => {
  return CURRENT_ENV.includes("production");
};

/**
 * Debug logging for non-production environments
 */
const debugLog = (message: string, data?: any): void => {
  if (!CURRENT_ENV.includes("production")) {
    console.log(`[Analytics Debug] ${message}`, data || "");
  }
};

/**
 * Crashlytics Service
 * Wrapper around Firebase Crashlytics SDK with environment gating
 */
class CrashlyticsService {
  private initialized = false;

  /**
   * Initialize Crashlytics (only in production)
   */
  async initialize(): Promise<void> {
    if (!isAnalyticsEnabled()) {
      debugLog("Crashlytics disabled in non-production environment");
      debugLog(`Current environment: ${CURRENT_ENV}`);
      return;
    }

    try {
      await FirebaseCrashlytics.setEnabled({ enabled: true });
      this.initialized = true;
      console.log("✅ Crashlytics initialized successfully");
    } catch (error) {
      console.error("❌ Error initializing Crashlytics:", error);
    }
  }

  /**
   * Check if service is ready
   */
  isReady(): boolean {
    return this.initialized && isAnalyticsEnabled();
  }

  /**
   * Set user identifier
   */
  async setUserId(userId: string): Promise<void> {
    if (!this.isReady()) {
      debugLog("Crashlytics setUserId skipped", { userId });
      return;
    }

    try {
      await FirebaseCrashlytics.setUserId({ userId });
      debugLog("Crashlytics user ID set", { userId });
    } catch (error) {
      console.error("Error setting Crashlytics user ID:", error);
    }
  }

  /**
   * Log a message for crash context
   */
  async log(message: string): Promise<void> {
    if (!this.isReady()) {
      debugLog("Crashlytics log skipped", { message });
      return;
    }

    try {
      await FirebaseCrashlytics.log({ message });
      debugLog("Crashlytics log recorded", { message });
    } catch (error) {
      console.error("Error logging to Crashlytics:", error);
    }
  }

  /**
   * Set a custom key-value pair
   */
  async setCustomKey(
    key: string,
    value: string | number | boolean
  ): Promise<void> {
    if (!this.isReady()) {
      debugLog("Crashlytics custom key skipped", { key, value });
      return;
    }

    try {
      // Determine type
      let type: "string" | "boolean" | "double" | "float" | "int" | "long";

      if (typeof value === "boolean") {
        type = "boolean";
      } else if (typeof value === "number") {
        type = Number.isInteger(value) ? "int" : "double";
      } else {
        type = "string";
        value = String(value);
      }

      await FirebaseCrashlytics.setCustomKey({
        key,
        value,
        type,
      });

      debugLog("Crashlytics custom key set", { key, value, type });
    } catch (error) {
      console.error("Error setting Crashlytics custom key:", error);
    }
  }

  /**
   * Record a non-fatal exception
   */
  async recordException(
    error: Error,
    context?: Record<string, any>
  ): Promise<void> {
    if (!this.isReady()) {
      debugLog("Crashlytics exception skipped", {
        error: error.message,
        context,
      });
      return;
    }

    try {
      // Log context as custom keys first
      if (context) {
        for (const [key, value] of Object.entries(context)) {
          await this.setCustomKey(`error_${key}`, String(value));
        }
      }

      // Record the exception
      await FirebaseCrashlytics.recordException({
        message: error.message,
        stacktrace: error.stack
          ? error.stack.split("\n").map((line) => ({
              functionName: "",
              fileName: "",
              lineNumber: 0,
              columnNumber: 0,
              raw: line.trim(),
            }))
          : undefined,
      });

      console.log("📊 Crashlytics exception recorded:", error.message);
    } catch (err) {
      console.error("Error recording Crashlytics exception:", err);
    }
  }

  /**
   * Force a crash (TESTING ONLY - DO NOT USE IN PRODUCTION CODE)
   */
  async forceCrash(): Promise<void> {
    if (!this.isReady()) {
      debugLog("Crashlytics force crash skipped (analytics disabled)");
      return;
    }

    console.warn("⚠️ Force crashing app for testing...");

    try {
      await FirebaseCrashlytics.crash({
        message: "Test crash - this is intentional",
      });
    } catch (error) {
      console.error("Error forcing crash:", error);
    }
  }
}

// Export singleton instance
export const crashlytics = new CrashlyticsService();

// Convenience functions for easier imports
export const initializeCrashlytics = () => crashlytics.initialize();
export const setCrashlyticsUserId = (userId: string) =>
  crashlytics.setUserId(userId);
export const logCrashlytics = (message: string) => crashlytics.log(message);
export const setCrashlyticsKey = (
  key: string,
  value: string | number | boolean
) => crashlytics.setCustomKey(key, value);
export const recordCrashlyticsError = (
  error: Error,
  context?: Record<string, any>
) => crashlytics.recordException(error, context);

export default crashlytics;
